import {
  Children, isValidElement, useCallback, useEffect, useLayoutEffect, useRef, useState,
  type ChangeEvent, type CSSProperties, type KeyboardEvent, type ReactElement, type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { CaretDown, Check, MagnifyingGlass } from "@phosphor-icons/react";

/**
 * Auswahlfeld statt `<select>` — übernommen aus lilasprojekte (components/ui/primitives.tsx,
 * `Select`). Die aufgeklappte Liste eines nativen `<select>` zeichnet das Betriebssystem:
 * weiß auf dunkler Oberfläche, nicht zu runden, nicht einzufärben.
 *
 * Die Schnittstelle bleibt die des nativen Feldes, damit ein Bildschirm nur das Tag
 * tauscht: `<option value>`-Kinder, `value`, und `onChange` bekommt ein Ereignis der Form
 * `{target: {value}}`. Anders als dort: Pfeiltasten auf dem Knopf, weil man auf der
 * Baustelle auch mit Tastatur am Tablet sitzt.
 */
const RAND = 8;
const BREITESTE_LISTE = 460;

interface Wahl { value: string; label: string; disabled?: boolean }

function text(n: ReactNode): string {
  if (n == null || typeof n === "boolean") return "";
  if (typeof n === "string" || typeof n === "number") return String(n);
  if (Array.isArray(n)) return n.map(text).join("");
  if (isValidElement(n)) return text((n.props as { children?: ReactNode }).children);
  return "";
}

function wahlen(children: ReactNode): Wahl[] {
  const aus: Wahl[] = [];
  Children.toArray(children).forEach((k) => {
    if (!isValidElement(k)) return;
    const el = k as ReactElement<{ value?: string | number; children?: ReactNode; disabled?: boolean }>;
    if (el.type === "option") {
      aus.push({ value: String(el.props.value ?? text(el.props.children)), label: text(el.props.children),
                 disabled: el.props.disabled });
    } else {
      aus.push(...wahlen(el.props.children));            // Fragment, optgroup
    }
  });
  return aus;
}

export interface AuswahlProps {
  value: string | null | undefined;
  onChange: (e: ChangeEvent<HTMLSelectElement>) => void;
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
  disabled?: boolean;
  id?: string;
  title?: string;
  "aria-label"?: string;
  /** Filterfeld über der Liste; ab 12 Einträgen von selbst an */
  suchbar?: boolean;
}

export function Auswahl({ value, onChange, children, className = "", style, disabled, id, title,
                          "aria-label": ariaLabel, suchbar }: AuswahlProps) {
  const liste = wahlen(children);
  const [offen, setOffen] = useState(false);
  const [q, setQ] = useState("");
  const [fokus, setFokus] = useState(0);
  const [pos, setPos] = useState<{ left: number; top: number; width: number; auf: boolean } | null>(null);
  const knopfRef = useRef<HTMLButtonElement>(null);
  const listeRef = useRef<HTMLDivElement>(null);
  const aktuell = liste.find((o) => o.value === (value ?? ""));
  const mitSuche = suchbar ?? liste.length > 12;
  const nadel = q.trim().toLowerCase();
  const sichtbar = nadel ? liste.filter((o) => o.label.toLowerCase().includes(nadel)) : liste;

  const platzieren = useCallback(() => {
    const r = knopfRef.current?.getBoundingClientRect();
    if (!r) return;
    const maxH = Math.min(300, liste.length * 44 + 12) + (mitSuche ? 50 : 0);
    const auf = r.bottom + maxH + 8 > window.innerHeight && r.top - maxH - 8 > 0;
    setPos({ left: r.left, top: auf ? r.top - 4 : r.bottom + 4, width: r.width, auf });
  }, [liste.length, mitSuche]);

  useLayoutEffect(() => {
    if (!offen) return;
    setQ("");
    setFokus(Math.max(0, liste.findIndex((o) => o.value === (value ?? ""))));
    platzieren();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offen, platzieren]);

  // Breiter als das Feld darf die Liste sein; ragt sie rechts hinaus, rückt sie nach links.
  useLayoutEffect(() => {
    const el = listeRef.current;
    if (!offen || !el || !pos) return;
    const zuviel = el.getBoundingClientRect().right - (window.innerWidth - RAND);
    if (zuviel <= 0.5) return;
    const links = Math.max(RAND, pos.left - zuviel);
    if (Math.abs(links - pos.left) > 0.5) setPos({ ...pos, left: links });
  }, [offen, pos]);

  useEffect(() => {
    listeRef.current?.querySelector<HTMLElement>(`[data-i="${fokus}"]`)?.scrollIntoView({ block: "nearest" });
  }, [fokus, offen]);

  useEffect(() => {
    if (!offen) return;
    const daneben = (e: Event) => {
      const z = e.target as Node;
      if (listeRef.current?.contains(z) || knopfRef.current?.contains(z)) return;
      setOffen(false);
    };
    // Rollen schließt nicht, es rückt nach — wie ein natives Feld.
    const rollen = (e: Event) => { if (!listeRef.current?.contains(e.target as Node)) platzieren(); };
    document.addEventListener("pointerdown", daneben);
    document.addEventListener("scroll", rollen, true);
    window.addEventListener("resize", platzieren);
    return () => {
      document.removeEventListener("pointerdown", daneben);
      document.removeEventListener("scroll", rollen, true);
      window.removeEventListener("resize", platzieren);
    };
  }, [offen, platzieren]);

  const nehmen = (o: Wahl | undefined) => {
    if (!o || o.disabled) return;
    if (o.value !== (value ?? "")) onChange({ target: { value: o.value } } as unknown as ChangeEvent<HTMLSelectElement>);
    setOffen(false);
    knopfRef.current?.focus();
  };

  const tasten = (e: KeyboardEvent) => {
    if (e.key === "Escape" && offen) {
      // Verbraucht: sonst schlösse dasselbe Escape auch den Dialog darüber.
      e.stopPropagation(); e.preventDefault();
      if (q) setQ(""); else { setOffen(false); knopfRef.current?.focus(); }
      return;
    }
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      if (!offen) { setOffen(true); return; }
      const d = e.key === "ArrowDown" ? 1 : -1;
      setFokus((f) => Math.min(sichtbar.length - 1, Math.max(0, f + d)));
      return;
    }
    if ((e.key === "Enter" || (e.key === " " && !mitSuche)) && offen) {
      e.preventDefault();
      nehmen(sichtbar[fokus]);
    }
  };

  return (
    <div className={`auswahl ${className}`} style={style} onKeyDown={tasten}>
      <button ref={knopfRef} type="button" className="eingabe auswahl-knopf" disabled={disabled} id={id}
              title={title ?? aktuell?.label} aria-label={ariaLabel} aria-haspopup="listbox" aria-expanded={offen}
              onClick={() => setOffen((o) => !o)}>
        <span className={aktuell ? "" : "dim"}>{aktuell?.label ?? ""}</span>
        <CaretDown size={14} className={`auswahl-pfeil${offen ? " offen" : ""}`} />
      </button>
      {offen && pos && createPortal(
        <div ref={listeRef} role="listbox" className="auswahl-liste" onKeyDown={tasten}
             style={{
               left: pos.left,
               top: pos.auf ? undefined : pos.top,
               bottom: pos.auf ? window.innerHeight - pos.top : undefined,
               minWidth: pos.width,
               maxWidth: Math.min(BREITESTE_LISTE, window.innerWidth - 2 * RAND),
             }}>
          {mitSuche && (
            <label className="auswahl-suche">
              <MagnifyingGlass size={15} />
              <input autoFocus value={q} placeholder="Suchen" aria-label="Suchen"
                     onChange={(e) => { setQ(e.target.value); setFokus(0); }} />
            </label>
          )}
          {sichtbar.length === 0 && <div className="auswahl-leer">Keine Treffer</div>}
          {sichtbar.map((o, i) => {
            const an = o.value === (value ?? "");
            return (
              <button key={o.value} type="button" role="option" aria-selected={an} data-i={i}
                      disabled={o.disabled} className={`auswahl-zeile${an ? " an" : ""}${i === fokus ? " fokus" : ""}`}
                      onMouseEnter={() => setFokus(i)} onClick={() => nehmen(o)}>
                {an ? <Check size={14} weight="bold" /> : <span className="auswahl-platz" />}
                <span>{o.label}</span>
              </button>
            );
          })}
        </div>,
        // In einem modalen <dialog> IN den Dialog: der liegt in der obersten Ebene, und
        // alles außerhalb ist gesperrt — am <body> ginge die Liste dahinter auf und
        // ließe sich nicht anklicken (Rolle im Beteiligten-Dialog, 24.10.2026).
        knopfRef.current?.closest("dialog") ?? document.body,
      )}
    </div>
  );
}
